import { useState } from 'react';
import { Modal } from '../../Modal';
import { Button, Switch, Typography } from '../ui';
import { devToolsUnlocked } from '../../../lib/dev-tools';
import { CREDIT_MINER_SPEC, setToggleOverride, toggleOverride } from '../../../lib/dev-feature-toggles';
import { PICTURE_SHAPES, playPictureArrivals, type PictureArrivalRun, type PictureShape } from '../../../lib/dev-picture-arrival';

// Dev-only lab for the moment a generated picture replaces its placeholder in the chat: pick a shape, how
// slow the network is, and whether the Credit Miner plays in the placeholder, then watch the real row play
// it out in the open chat (see lib/dev-picture-arrival.ts). Open a chat first — the row lands in it.

const REDIRECT_MS = 430;

/** Gate #3 (BrandHeader and DevMenu check it too): never renders without a dev build or a grant. */
export function DevPictureArrivalPanel(props: { open: boolean; onClose: () => void }) {
  if (!devToolsUnlocked()) return null;
  return <PictureArrivalPanel {...props} />;
}

function PictureArrivalPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [shape, setShape] = useState<PictureShape>(PICTURE_SHAPES[0]);
  const [imageDelayMs, setImageDelayMs] = useState(1000);
  const [renderMs, setRenderMs] = useState(6000);
  const [redirectHop, setRedirectHop] = useState(true);
  const [game, setGame] = useState(() => toggleOverride(CREDIT_MINER_SPEC) === 'true');

  const withGame = (next: boolean) => {
    setGame(next);
    setToggleOverride(CREDIT_MINER_SPEC, next ? 'true' : 'false', false);
  };
  const run = (runs: PictureArrivalRun[]) => {
    setToggleOverride(CREDIT_MINER_SPEC, game ? 'true' : 'false', false);
    onClose();
    playPictureArrivals(runs);
  };
  const settings = { imageDelayMs, renderMs, redirectHop };

  return (
    <Modal open={open} onClose={onClose} width={560} title="Picture arrival" footer={<Button onClick={onClose}>Done</Button>}>
      <Typography variant="p3" color="secondary">
        Plays a generated picture landing in the open chat, as production delivers it: the reply appears with its placeholder,
        the address arrives after the render time, and the bytes after the network delay. Local to this page — nothing is sent,
        and the Credit Miner&apos;s save and claims are answered in memory.
      </Typography>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
        {PICTURE_SHAPES.map((s) => (
          <Button key={s.id} size="small" variant={s.id === shape.id ? 'contained' : 'outlined'} onClick={() => setShape(s)}>
            {s.label}
          </Button>
        ))}
      </div>

      <label style={{ display: 'block', marginTop: 16 }}>
        <Typography variant="p3" color="primary" component="span">
          Network delay before the picture&apos;s bytes: {(imageDelayMs / 1000).toFixed(2)}s
        </Typography>
        <input type="range" min={0} max={3000} step={250} value={imageDelayMs} onChange={(e) => setImageDelayMs(Number(e.target.value))} style={{ display: 'block', width: '100%' }} />
      </label>
      <label style={{ display: 'block', marginTop: 12 }}>
        <Typography variant="p3" color="primary" component="span">
          Render time before the address arrives: {(renderMs / 1000).toFixed(1)}s
        </Typography>
        <input type="range" min={0} max={20000} step={500} value={renderMs} onChange={(e) => setRenderMs(Number(e.target.value))} style={{ display: 'block', width: '100%' }} />
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        <Switch checked={redirectHop} onChange={setRedirectHop} label="Redirect hop" description={`The share link's uncached 302 in front of the bytes (+${REDIRECT_MS}ms), as in production`} />
        <Switch checked={game} onChange={withGame} label="Credit Miner in the placeholder" description="Off shows the shimmer every other account sees" />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <Button onClick={() => run([{ shape, ...settings }])}>Run in chat</Button>
        <Button variant="outlined" onClick={() => run(PICTURE_SHAPES.map((s) => ({ shape: s, ...settings })))}>
          Run every shape
        </Button>
      </div>
    </Modal>
  );
}
